import { useMemo } from 'react';

import { IS_DEV, IS_TEST } from 'env';
import i18n from 'i18next';
import detector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

export enum LocaleKey {
    FR = 'fr',
    EN = 'en',
}

export const DEFAULT_LOCALE = LocaleKey.EN;

export const supportedLocales = [DEFAULT_LOCALE, LocaleKey.FR];

export const useLanguageOptions = () => {
    return useMemo(
        () =>
            supportedLocales.map(supportedLocaleKey => ({
                code: supportedLocaleKey,
                name: i18n.t<string>(`locale.${supportedLocaleKey}`),
            })),
        []
    );
};

export const isSupportedLocale = (localeKey: string) => {
    return supportedLocales.find(supportedLocale => supportedLocale === localeKey);
};

const i18nDefaultConfiguration: i18n.InitOptions = {
    debug: false,

    // lng: 'fr', // Language is detected automatically by browser settings
    ns: ['manual', 'default', 'cpq', 'fileImport'],
    defaultNS: 'default',
    fallbackNS: 'manual',
    fallbackLng: DEFAULT_LOCALE,
    react: {
        wait: true,
        bindI18n: 'languageChanged loaded',
        bindStore: 'added removed',
    },
    keySeparator: false, // we do not use keys in form messages.welcome
    interpolation: {
        escapeValue: false, // react already safes from xss
    },
    backend: {
        loadPath: `${window.location.origin}/locales/{{lng}}/{{ns}}.json`,
        ...(IS_DEV
            ? {
                  requestOptions: {
                      cache: 'no-store',
                  },
              }
            : {}),
    },
    returnEmptyString: false,
};

interface HttpBackendWithOptions {
    options?: {
        queryStringParams?: Record<string, string>;
    };
}

function setupDevLocaleHmr() {
    if (!IS_DEV || !import.meta.hot) {
        return;
    }

    import.meta.hot.on('locales:changed', () => {
        const backend = i18n.services.backendConnector.backend as
            | HttpBackendWithOptions
            | undefined;

        if (backend?.options) {
            backend.options.queryStringParams = {
                ...backend.options.queryStringParams,
                _t: String(Date.now()),
            };
        }

        i18n.reloadResources().catch(err => {
            console.error('Failed to reload i18n resources', err);
        });
    });
}

const initChain = i18n
    .use(Backend)
    .use(detector)
    .use(initReactI18next)
    .init(i18nDefaultConfiguration);

// Auto-detect user language from persisted auth data. Skipped in tests for
// two reasons: (1) it adds async noise to module init that races with test
// rendering; (2) `redesign/helpers/auth` statically imports `DEFAULT_LOCALE`
// from THIS file, forming a circular module graph — Vitest's ESM loader
// resolves the binding to `undefined` in workers when the cycle is entered
// through `helpers/i18n`, producing "restoreAuthData is not a function"
// unhandled rejections even through a dynamic import. Tests override the
// language explicitly via `i18next.changeLanguage` when they need to.
if (!IS_TEST) {
    initChain.then(async () => {
        setupDevLocaleHmr();

        try {
            const { restoreAuthData } = await import('redesign/helpers/auth');
            const authData = restoreAuthData();
            if (authData && authData.user) {
                i18n.changeLanguage(authData.user.language);
            }
        } catch {
            // Swallow — failure to restore the persisted language is
            // non-critical: i18next falls back to `DEFAULT_LOCALE`.
        }
    });
}

export default i18n;
